<template>
  <div class="yapp-root">
    <!-- Header -->
    <div class="yapp-header">
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-chat</v-icon>
        <span class="text-white font-weight-bold text-body-1">Yapp</span>
      </div>

      <div class="d-flex align-center">
        <!-- Clinic selector (admin only) -->
        <v-menu v-if="hasAllClinicsAccess" location="bottom end">
          <template #activator="{ props }">
            <v-btn variant="text" color="white" v-bind="props" size="small">
              <v-icon size="small" class="mr-1">mdi-hospital-building</v-icon>
              <span class="d-none d-sm-inline">{{ currentClinicLabel }}</span>
              <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item
              @click="onClinicChange(null)"
              :class="{ 'bg-primary-lighten-4': !selectedClinicId }"
            >
              <v-list-item-title>Todas las clinicas</v-list-item-title>
              <template #append v-if="!selectedClinicId">
                <v-icon color="primary" size="small">mdi-check</v-icon>
              </template>
            </v-list-item>
            <v-divider />
            <v-list-item
              v-for="clinic in clinicsStore.clinics"
              :key="clinic.id"
              @click="onClinicChange(clinic.id)"
              :class="{ 'bg-primary-lighten-4': selectedClinicId === clinic.id }"
            >
              <v-list-item-title>
                {{ clinic.name || clinic.clinic_name || `Clinica ${clinic.id}` }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ clinic.city || clinic.location || '' }}
              </v-list-item-subtitle>
              <template #append v-if="selectedClinicId === clinic.id">
                <v-icon color="primary" size="small">mdi-check</v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- User menu -->
        <v-menu location="bottom end">
          <template #activator="{ props }">
            <v-btn icon variant="text" color="white" v-bind="props" size="small" class="ml-1">
              <v-avatar size="28" color="rgba(255,255,255,0.2)">
                <span class="text-caption font-weight-bold text-white">
                  {{ userInitials }}
                </span>
              </v-avatar>
            </v-btn>
          </template>
          <v-card min-width="200">
            <v-card-text class="pb-0">
              <div class="text-subtitle-2 font-weight-medium">
                {{ authStore.user?.full_name || authStore.user?.username || 'Usuario' }}
              </div>
              <div class="text-caption text-grey">
                {{ authStore.userRole }}
              </div>
            </v-card-text>
            <v-divider class="mt-2" />
            <v-list density="compact">
              <v-list-item @click="handleLogout" prepend-icon="mdi-logout" title="Cerrar sesion" />
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- Chat content (full height remaining) -->
    <div class="yapp-body">
      <router-view
        :selected-clinic-id="selectedClinicId"
        @clinic-change="onClinicChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useClinicsStore } from '@/stores/clinics'
import { useChatStore } from '@/stores/chat'

const router = useRouter()
const authStore = useAuthStore()
const clinicsStore = useClinicsStore()
const chatStore = useChatStore()

const selectedClinicId = ref(null)

const hasAllClinicsAccess = computed(() => authStore.hasAllClinicsAccess)

const currentClinicLabel = computed(() => {
  if (!selectedClinicId.value) return 'Todas'
  const clinic = clinicsStore.getClinicById(selectedClinicId.value)
  return clinic?.name || clinic?.clinic_name || `Clinica ${selectedClinicId.value}`
})

const userInitials = computed(() => {
  const name = authStore.user?.full_name || authStore.user?.username || '?'
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
})

const onClinicChange = (clinicId) => {
  selectedClinicId.value = clinicId
  chatStore.setCurrentClinic(clinicId)
}

const handleLogout = async () => {
  await authStore.logout()
  router.push({ name: 'Login' })
}

onMounted(async () => {
  await authStore.initializeAuth()

  if (!authStore.isAuthenticated) {
    router.push({ name: 'Login' })
    return
  }

  if (!clinicsStore.clinics.length) {
    try {
      await clinicsStore.fetchClinics()
    } catch (error) {
      console.error('Error loading clinics:', error)
    }
  }
})
</script>

<style scoped>
.yapp-root {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f0f2f5;
}

.yapp-header {
  background: #075E54;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.yapp-body {
  flex: 1;
  overflow: hidden;
}
</style>
